import { GraphQLClient } from 'graphql-request'

const endpoint = process.env.REACT_APP_DATO_CMS_ENDPOINT || 'https://graphql.datocms.com/'

export const client = new GraphQLClient(endpoint, {
  headers: {
    authorization: `Bearer ${process.env.REACT_APP_DATO_CMS_API_TOKEN}`,
  },
})

export const fetchSolutions = async () => {
  const query = `
    query Solutions {
      allSolutions(orderBy: position_ASC) {
        id
        title
        slug
        description
        shortDescription
        icon {
          url
          alt
        }
        features {
          id
          title
          description
        }
        benefits
        useCases
        ctaText
        ctaLink
        image {
          url
          alt
        }
        position
      }
    }
  `

  try {
    const data = await client.request(query)
    return data.allSolutions
  } catch (error) {
    console.error('Error fetching solutions from DatoCMS:', error)
    return []
  }
}

export const fetchSolutionBySlug = async (slug) => {
  const query = `
    query Solution($slug: String!) {
      solution(filter: {slug: {eq: $slug}}) {
        id
        title
        slug
        description
        shortDescription
        icon {
          url
          alt
        }
        features {
          id
          title
          description
        }
        benefits
        useCases
        ctaText
        ctaLink
        image {
          url
          alt
        }
        relatedSolutions {
          id
          title
          slug
          shortDescription
          icon {
            url
            alt
          }
        }
      }
    }
  `

  try {
    const data = await client.request(query, { slug })
    return data.solution
  } catch (error) {
    console.error('Error fetching solution from DatoCMS:', error)
    return null
  }
}

export const fetchIndustries = async () => {
  const query = `
    query Industries {
      allIndustries(orderBy: position_ASC) {
        id
        name
        slug
        description
        shortDescription
        icon {
          url
          alt
        }
        image {
          url
          alt
        }
        benefits
        useCases
        position
      }
    }
  `

  try {
    const data = await client.request(query)
    return data.allIndustries
  } catch (error) {
    console.error('Error fetching industries from DatoCMS:', error)
    return []
  }
}

export const fetchIndustryBySlug = async (slug) => {
  const query = `
    query Industry($slug: String!) {
      industry(filter: {slug: {eq: $slug}}) {
        id
        name
        slug
        description
        shortDescription
        icon {
          url
          alt
        }
        image {
          url
          alt
        }
        benefits
        useCases
        relatedIndustries {
          id
          name
          slug
          shortDescription
          icon {
            url
            alt
          }
        }
      }
    }
  `

  try {
    const data = await client.request(query, { slug })
    return data.industry
  } catch (error) {
    console.error('Error fetching industry from DatoCMS:', error)
    return null
  }
}

// Customer Types queries
export const fetchCustomerTypes = async () => {
  const query = `
    query CustomerTypes {
      allCustomerTypes(orderBy: position_ASC) {
        id
        title
        slug
        description
        shortDescription
        icon {
          url
          alt
        }
        image {
          url
          alt
        }
        features {
          id
          title
          description
        }
        benefits
        useCases
        ctaText
        ctaLink
        position
      }
    }
  `

  try {
    const data = await client.request(query)
    return data.allCustomerTypes
  } catch (error) {
    console.error('Error fetching customer types from DatoCMS:', error)
    return []
  }
}

export const fetchCustomerTypeBySlug = async (slug) => {
  const query = `
    query CustomerType($slug: String!) {
      customerType(filter: {slug: {eq: $slug}}) {
        id
        title
        slug
        description
        shortDescription
        icon {
          url
          alt
        }
        image {
          url
          alt
        }
        features {
          id
          title
          description
        }
        benefits
        useCases
        testimonials {
          id
          author
          company
          content
          avatar {
            url
            alt
          }
        }
        ctaText
        ctaLink
        relatedCustomerTypes {
          id
          title
          slug
          shortDescription
          icon {
            url
            alt
          }
        }
      }
    }
  `

  try {
    const data = await client.request(query, { slug })
    return data.customerType
  } catch (error) {
    console.error('Error fetching customer type from DatoCMS:', error)
    return null
  }
}

// Resources queries
export const fetchResources = async () => {
  const query = `
    query Resources {
      allResources(orderBy: publishedDate_DESC) {
        id
        title
        slug
        type
        description
        excerpt
        author
        publishedDate
        thumbnail {
          url
          alt
        }
        downloadUrl
        videoUrl
        category
        tags
        featured
      }
    }
  `

  try {
    const data = await client.request(query)
    return data.allResources
  } catch (error) {
    console.error('Error fetching resources from DatoCMS:', error)
    return []
  }
}

export const fetchResourceBySlug = async (slug) => {
  const query = `
    query Resource($slug: String!) {
      resource(filter: {slug: {eq: $slug}}) {
        id
        title
        slug
        type
        description
        content
        author
        publishedDate
        thumbnail {
          url
          alt
        }
        downloadUrl
        videoUrl
        category
        tags
        relatedResources {
          id
          title
          slug
          type
          excerpt
          thumbnail {
            url
            alt
          }
        }
      }
    }
  `

  try {
    const data = await client.request(query, { slug })
    return data.resource
  } catch (error) {
    console.error('Error fetching resource from DatoCMS:', error)
    return null
  }
}

// Pricing queries
export const fetchPricingPlans = async () => {
  const query = `
    query PricingPlans {
      allPricingPlans(orderBy: price_ASC) {
        id
        name
        slug
        price
        billingPeriod
        description
        features
        highlighted
        ctaText
        ctaLink
      }
    }
  `

  try {
    const data = await client.request(query)
    return data.allPricingPlans
  } catch (error) {
    console.error('Error fetching pricing plans from DatoCMS:', error)
    return []
  }
}

// Company/About queries
export const fetchCompanyInfo = async () => {
  const query = `
    query CompanyInfo {
      companyInfo {
        mission
        vision
        values {
          id
          title
          description
          icon {
            url
            alt
          }
        }
        teamMembers {
          id
          name
          role
          bio
          photo {
            url
            alt
          }
          linkedin
          twitter
        }
        milestones {
          id
          year
          title
          description
        }
      }
    }
  `

  try {
    const data = await client.request(query)
    return data.companyInfo
  } catch (error) {
    console.error('Error fetching company info from DatoCMS:', error)
    return null
  }
}